import { runSimulation, type SimulationConfig, type SimulationResult } from "./runSimulation";

export type MonteCarloResult = {
  runs: number;
  config: SimulationConfig;
  /** Per-match: mean ± percentile bands for avg skill error */
  convergenceBands: {
    match: number;
    mean: number;
    p5: number;
    p95: number;
  }[];
  /** Per-match: sigma bands */
  sigmaBands: {
    match: number;
    mean: number;
    p5: number;
    p95: number;
  }[];
  /** Summary stats across runs */
  summaryStats: {
    skillCorrelation: { mean: number; p5: number; p95: number };
    pointsCorrelation: { mean: number; p5: number; p95: number };
    gini: { mean: number; p5: number; p95: number };
    predictionAccuracy: { mean: number; p5: number; p95: number };
    convergenceMatch: { mean: number; p5: number; p95: number };
  };
  /** Individual run results for deeper analysis */
  individualResults: SimulationResult[];
};

const percentile = (sorted: number[], p: number): number => {
  if (sorted.length === 0) return 0;
  const idx = (p / 100) * (sorted.length - 1);
  const lower = Math.floor(idx);
  const upper = Math.ceil(idx);
  if (lower === upper) return sorted[lower]!;
  return sorted[lower]! * (upper - idx) + sorted[upper]! * (idx - lower);
};

const stats = (values: number[]): { mean: number; p5: number; p95: number } => {
  const sorted = [...values].sort((a, b) => a - b);
  const mean = values.reduce((s, v) => s + v, 0) / values.length;
  return {
    mean,
    p5: percentile(sorted, 5),
    p95: percentile(sorted, 95),
  };
};

export const runMonteCarlo = (
  config: SimulationConfig,
  runs: number = 10,
  onProgress?: (completed: number, total: number) => void,
): MonteCarloResult => {
  const results: SimulationResult[] = [];

  for (let i = 0; i < runs; i++) {
    results.push(runSimulation(config));
    onProgress?.(i + 1, runs);
  }

  // Find min match count across all runs
  const minMatches = Math.min(...results.map((r) => r.matchSnapshots.length));

  // Convergence bands (avg skill error per match)
  const convergenceBands: MonteCarloResult["convergenceBands"] = [];
  for (let m = 0; m < minMatches; m++) {
    const errors = results.map((r) => {
      const snap = r.matchSnapshots[m]!;
      const errs = snap.playerSnapshots.map((p) => Math.abs(p.trueSkill - p.estimatedSkill));
      return errs.reduce((s, e) => s + e, 0) / errs.length;
    });
    const s = stats(errors);
    convergenceBands.push({ match: m + 1, ...s });
  }

  // Sigma bands
  const sigmaBands: MonteCarloResult["sigmaBands"] = [];
  for (let m = 0; m < minMatches; m++) {
    const sigmas = results.map((r) => r.analytics.sigmaOverTime[m] ?? 0);
    const s = stats(sigmas);
    sigmaBands.push({ match: m + 1, ...s });
  }

  // Summary stats
  const summaryStats = {
    skillCorrelation: stats(results.map((r) => r.analytics.skillCorrelation)),
    pointsCorrelation: stats(results.map((r) => r.analytics.pointsCorrelation)),
    gini: stats(results.map((r) => r.analytics.giniCoefficient)),
    predictionAccuracy: stats(results.map((r) => r.analytics.predictionAccuracy)),
    convergenceMatch: stats(results.map((r) => r.analytics.convergenceMatch)),
  };

  return {
    runs,
    config,
    convergenceBands,
    sigmaBands,
    summaryStats,
    individualResults: results,
  };
};
